"use client";

import { useState } from "react";
import { universTech, fichesPremium, formatPrice } from "@/lib/data";
import { useCart, useUI } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, ShoppingBag, Cpu, Crown, Sparkles } from "lucide-react";
import Image from "next/image";

/* ═══════════ Gabarit carte tech (calqué sur Produits du Moment) ═══════════ */
function TechCard({ p }: { p: typeof universTech[number] }) {
  const addItem = useCart((s) => s.addItem);
  const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
  return (
    <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 flex flex-col">
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <Image
          src={p.image}
          alt={p.name}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-700"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
        />
        {p.badge && (
          <Badge className="absolute top-3 left-3 bg-gradient-to-r from-[#006B3F] to-[#008751] text-white border-0 text-[10px] font-black tracking-wider">
            {p.badge}
          </Badge>
        )}
        {discount > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#CE1126] text-white text-[11px] font-black">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[10px] font-bold tracking-wider text-gray-400 uppercase">{p.category}</p>
        <h3 className="font-bold text-gray-900 mt-1 leading-snug">{p.name}</h3>
        <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2 flex-1">{p.description}</p>
        <div className="mt-4 flex items-end justify-between gap-2">
          <div>
            <p className="text-lg font-black text-[#006B3F]">
              {formatPrice(p.price)} <span className="text-[10px] font-bold text-gray-400">FCFA</span>
            </p>
            {p.oldPrice && (
              <p className="text-xs text-gray-400 line-through">{formatPrice(p.oldPrice)} FCFA</p>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => addItem({ id: p.id, name: p.name, price: p.price, image: p.image })}
            className="bg-gradient-to-r from-[#006B3F] to-[#008751] hover:shadow-lg text-white shrink-0"
          >
            <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Acheter
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════ UNIVERS TECH ═══════════════════ */
export function UniversTech() {
  const openBoutique = useUI((s) => s.openBoutique);
  const vedettes = universTech.slice(0, 8);

  return (
    <section id="univers-tech" className="py-16 sm:py-20 bg-white relative overflow-hidden">
      {/* halos tech */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 40% 30% at 90% 5%, rgba(0,107,63,0.08), transparent), radial-gradient(ellipse 35% 25% at 5% 95%, rgba(255,215,0,0.10), transparent)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.3em] text-[#006B3F] uppercase mb-3 flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5" /> L&apos;Univers Technologique STS
            </p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Univers{" "}
              <span className="bg-gradient-to-r from-[#006B3F] via-[#008751] to-[#FFD700] bg-clip-text text-transparent">
                Tech
              </span>
            </h2>
            <div className="senegal-bar mt-5" />
            <p className="mt-3 text-gray-500 leading-relaxed">
              Smartphones, ordinateurs, audio, TV, montres, réseaux et domotique :{" "}
              <strong className="text-gray-700">240 fiches technologiques</strong> sélectionnées au
              gabarit d&apos;excellence STS, mises à jour chaque semaine.
            </p>
          </div>
          <Button
            onClick={() => openBoutique("tech")}
            className="bg-gradient-to-r from-[#006B3F] to-[#008751] hover:shadow-xl text-white shrink-0 h-11 px-6"
          >
            <Cpu className="w-4 h-4 mr-2" /> Explorer l&apos;Univers Tech (240)
          </Button>
        </div>

        {/* Bandeau stats trésors */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {[
            { k: "1000", v: "Trésors signés" },
            { k: "240", v: "Fiches Univers Tech" },
            { k: "36", v: "Fiches Premium" },
            { k: "70+", v: "Marques partenaires" },
          ].map((s) => (
            <div
              key={s.v}
              className="rounded-2xl border border-[#006B3F]/15 bg-gradient-to-b from-[#F5FBF7] to-white px-4 py-4 text-center"
            >
              <p className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-[#006B3F] to-[#008751] bg-clip-text text-transparent">
                {s.k}
              </p>
              <p className="text-[11px] font-bold tracking-wider text-gray-500 uppercase mt-1">{s.v}</p>
            </div>
          ))}
        </div>

        {/* Grille vedettes tech */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {vedettes.map((p) => (
            <TechCard key={p.id} p={p} />
          ))}
        </div>

        <div className="text-center mt-9">
          <Button
            onClick={() => openBoutique("tech")}
            variant="outline"
            className="border-[#006B3F]/30 text-[#006B3F] hover:bg-[#006B3F] hover:text-white h-11 px-8"
          >
            Voir les 240 fiches technologiques →
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════ 36 FICHES PREMIUM ═══════════════════ */
function PremiumCard({ p }: { p: typeof fichesPremium[number] }) {
  const addItem = useCart((s) => s.addItem);
  const discount = p.oldPrice ? Math.round((1 - p.price / p.oldPrice) * 100) : 0;
  return (
    <div className="group bg-gradient-to-b from-white to-[#FDF9EE] rounded-3xl overflow-hidden border border-[#DAA520]/20 shadow-sm hover:shadow-2xl hover:border-[#DAA520]/50 transition-all duration-500 hover:-translate-y-1.5 flex flex-col">
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <Image
          src={p.image}
          alt={p.name}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-700"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
        <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-black tracking-widest bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white shadow-lg">
          {p.badge}
        </span>
        {discount > 0 && (
          <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-[#CE1126] text-white text-[11px] font-black">
            -{discount}%
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-[10px] font-bold tracking-wider text-[#B8860B] uppercase">{p.category}</p>
        <h3 className="font-bold text-gray-900 mt-1 leading-snug">{p.name}</h3>
        {p.rating && (
          <div className="flex items-center gap-1.5 mt-2">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={`w-3.5 h-3.5 ${
                    i < Math.round(p.rating!)
                      ? "fill-[#FFD700] text-[#FFD700]"
                      : "fill-gray-200 text-gray-200"
                  }`}
                />
              ))}
            </div>
            <span className="text-xs font-bold text-gray-700">{p.rating}</span>
            <span className="text-xs text-gray-400">({p.reviews})</span>
          </div>
        )}
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            <p className="text-lg font-black text-gray-900">
              {formatPrice(p.price)} <span className="text-[10px] font-bold text-gray-400">FCFA</span>
            </p>
            {p.oldPrice && (
              <p className="text-xs text-gray-400 line-through">{formatPrice(p.oldPrice)} FCFA</p>
            )}
          </div>
          <Button
            size="sm"
            onClick={() =>
              addItem({
                id: p.id,
                name: p.name,
                price: p.price,
                image: p.image,
                presidential: p.price >= 7000000,
              })
            }
            className="bg-gradient-to-r from-[#B8860B] to-[#DAA520] hover:shadow-lg text-white shrink-0"
          >
            <Crown className="w-3.5 h-3.5 mr-1" /> Acquérir
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Premium36() {
  const [open, setOpen] = useState(false);
  const vedettes = fichesPremium.slice(0, 12);

  return (
    <section id="premium36" className="py-16 sm:py-20 bg-[#FDF9EE] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 45% 35% at 80% 10%, rgba(218,165,32,0.14), transparent), radial-gradient(ellipse 40% 30% at 10% 90%, rgba(0,107,63,0.08), transparent)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-[0.3em] text-[#B8860B] uppercase mb-3 flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Édition Prestige Numérotée
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
            36 Fiches{" "}
            <span className="bg-gradient-to-r from-[#B8860B] via-[#DAA520] to-[#FFD700] bg-clip-text text-transparent">
              Premium
            </span>
          </h2>
          <div className="senegal-bar mx-auto mt-5" />
          <p className="mt-4 text-gray-500 leading-relaxed">
            Le sommet absolu du catalogue : 36 trésors d&apos;exception en éditions Platinum,
            Numérotées et Prestige Sénégal — coffrets, certificats d&apos;authenticité et ligne
            concierge dédiée.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {vedettes.map((p) => (
            <PremiumCard key={p.id} p={p} />
          ))}
        </div>

        <div className="text-center mt-9">
          <Button
            onClick={() => setOpen(true)}
            className="bg-gradient-to-r from-[#B8860B] to-[#DAA520] hover:shadow-xl text-white h-11 px-8 font-black"
          >
            <Crown className="w-4 h-4 mr-2" /> Découvrir les 36 Fiches Premium
          </Button>
        </div>
      </div>

      {/* Dialog des 36 fiches premium */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-5xl lg:max-w-6xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <div className="relative bg-gradient-to-r from-[#171106] to-[#2A1D08] text-white px-6 py-5 shrink-0">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(ellipse 55% 80% at 85% 20%, rgba(255,215,0,0.25), transparent)",
              }}
            />
            <DialogHeader className="relative">
              <p className="text-[10px] font-black tracking-[0.3em] text-[#FFD700] uppercase">
                Édition Prestige Numérotée
              </p>
              <DialogTitle className="text-2xl font-black text-white mt-1">
                Les 36 Fiches Premium
              </DialogTitle>
              <DialogDescription className="text-white/60 text-sm mt-0.5">
                Platine massif, plaques gravées, coffrets d&apos;exception et certificats — livrées
                par concierge blanc.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-[#FDF9EE] to-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {fichesPremium.map((p) => (
                <PremiumCard key={p.id} p={p} />
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
